import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

import { tinaField as TinaField } from "tinacms/dist/tina-field";

import { client } from "./tina/__generated__/client";
import * as Lucide from "lucide-react";
import { RESOURCE_TYPES, type ResourceType } from "./lib/resource-types";
import type { Metadata } from "next";

import {
  buildMetadata,
  type BuildMetadataOptions,
} from "./lib/seo/metadata";
import { getGlobalRecord } from "./lib/seo/global";

export const tinaClient = client;
export const tinaField = TinaField;

type TinaReact = typeof import("tinacms/dist/react");

function loadTinaReact(): TinaReact {
  // tinacms/dist/react calls createContext at module load. Server actions
  // import other helpers from this file, so the React entry stays unloaded
  // until a client component actually calls useTina.
  return require("tinacms/dist/react");
}

export const useTina: TinaReact["useTina"] = (props) => {
  return loadTinaReact().useTina(props);
};

// Wrapper to handle type casting for tinaField
export const tinaFieldSafe = (object, field) => {
  return TinaField(object, field);
};

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}


export const DEFAULT_SEO = {
  siteName: "Binarly",
  title: "Binarly",
  description:
    "Binarly's Transparency Platform offers firmware security for supply chain risk management. Gain visibility into threats and recommendations on remediation.",
  keywords: "",
  image: "",
};

/**
 * Loads Global SEO when `globalSeo` is omitted, then builds Next metadata.
 * Pass `globalSeo: null` to skip loading the global Tina record (rare).
 */
export async function formatMetadata(
  options: BuildMetadataOptions
): Promise<Metadata> {
  const { globalSeo, ...rest } = options;
  const resolvedGlobal =
    globalSeo === undefined ? await getGlobalRecord() : globalSeo;
  return buildMetadata({
    ...rest,
    globalSeo: resolvedGlobal ?? undefined,
  });
}

export { buildMetadata } from "./lib/seo/metadata";

export function highlightText(text, colorClassName = "text-primary-300") {
  // Handle string input (Tiptap JSON as string)
  if (typeof text === "string") {
    try {
      const parsed = JSON.parse(text);
      return highlightTextFromTiptap(parsed, colorClassName);
    } catch (e) {
      // If not JSON, return as is
      return text;
    }
  }

  // Handle Tiptap JSON format
  if (text?.type === "doc" && text?.content) {
    return highlightTextFromTiptap(text, colorClassName);
  }

  // Handle old Tina format (for backward compatibility)
  if (!text?.children?.[0]?.children) {
    return text?.text || "";
  }

  return text.children[0].children
    .map((child) => {
      if (child.bold) {
        return `<span class="${colorClassName}">${child.text}</span>`;
      }
      return child.text;
    })
    .join("");
}

function highlightTextFromTiptap(json, colorClassName = "text-primary-300") {
  if (!json?.content) return "";

  const processNode = (node) => {
    if (node.type === "text") {
      let text = node.text || "";
      if (node.marks) {
        const hasBold = node.marks.some((mark) => mark.type === "bold");
        if (hasBold) {
          return `<span class="${colorClassName}">${text}</span>`;
        }
      }
      return text;
    }

    if (node.content) {
      return node.content.map((child) => processNode(child)).join("");
    }

    return "";
  };

  return json.content.map((node) => processNode(node)).join("");
}

interface ResourcesFilterParams {
  types?: ResourceType[];
  categories?: string[];
  tags?: string[];
  search?: string;
  sort?: string;
  limit?: number;
  /** Draft Mode: include unpublished drafts alongside published, uncached. */
  forPreview?: boolean;
  /** Skip category/tag connections when the caller only needs resource edges. */
  includeTaxonomy?: boolean;
}

/**
 * Any key of `ResourcesFilter` works. The key does not scope the results - see
 * the note in `fetchResourcesListData` - `templateType` does.
 */
const UNION_FILTER_KEY = "blog";

/** Tina union filters OR branches; dedupe by node id when a doc matches more than once. */
export function dedupeResourceEdges(
  edges: any[] | null | undefined
): any[] {
  if (!edges) return [];
  const seen = new Set<string>();
  const out: any[] = [];
  for (const edge of edges) {
    if (edge == null) continue;
    const id = edge.node?.id;
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(edge);
  }
  return out;
}

// Lightweight query for list views - only fetches essential fields, no heavy content
// Uses custom query defined in tina/queries/resourcesList.gql
export async function fetchResourcesListData({
  types = ["all"],
  categories = [],
  tags = [],
  search = "",
  sort = "publishDate",
  limit = 1000,
  forPreview = false,
  includeTaxonomy = true,
}: ResourcesFilterParams = {}) {
  const categoryFilter =
    categories.length > 0
      ? {
        categories: {
          category: {
            categories: {
              category: {
                categorySlug:
                  categories.length === 1
                    ? { eq: categories[0] }
                    : { in: categories },
              },
            },
          },
        },
      }
      : null;

  const tagFilter =
    tags.length > 0
      ? {
        tags: {
          tag: {
            tags: {
              tag: {
                tagSlug:
                  tags.length === 1 ? { eq: tags[0] } : { in: tags },
              },
            },
          },
        },
      }
      : null;

  const templateTypes = types.includes("all")
    ? RESOURCE_TYPES.map((type) => type.value)
    : types.filter((type) => type !== "all");

  // Tina resolves a union filter from a single template key and applies those
  // conditions across the whole connection, so listing several keys is
  // unreliable: with one key per type it silently answers from just one of
  // them. Send one key and scope by `templateType` instead - verified to return
  // identical results to the per-key form for published listings.
  const filter = {
    [UNION_FILTER_KEY]: {
      // Draft Mode wants published and unpublished in one listing, so drop the
      // condition rather than inverting it.
      ...(forPreview ? {} : { published: { eq: true } }),
      templateType: { in: templateTypes },
      ...(categoryFilter || {}),
      ...(tagFilter || {}),
      ...(search && {
        title: { startsWith: search },
      }),
    },
  };

  // Use custom query from tina/queries/resourcesList.gql
  const resourcesData = await tinaClient.queries.resourcesList(
    {
      filter,
      sort,
      last: limit,
    },
    forPreview
      ? { fetchOptions: { cache: "no-store" as RequestCache } }
      : undefined
  );

  if (resourcesData?.data?.resourcesConnection?.edges) {
    resourcesData.data.resourcesConnection.edges = dedupeResourceEdges(
      resourcesData.data.resourcesConnection.edges
    );
  }

  if (!includeTaxonomy) {
    return { resourcesData };
  }

  const [categoriesData, tagsData] = await Promise.all([
    tinaClient.queries.categoriesConnection(),
    tinaClient.queries.tagsConnection(),
  ]);

  return {
    resourcesData,
    categoriesData,
    tagsData,
  };
}

export function getIcon(iconString) {
  if (!iconString) {
    return null;
  }

  const iconName = iconString
    ?.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase())
    .replace(/^[a-z]/, (letter) => letter.toUpperCase());
  const Icon = Lucide?.[iconName] || null;

  return Icon;
}

/**
 * Extract plain text from Tiptap JSON content
 * Recursively processes all nodes and extracts text content
 */
export function extractTextFromTiptap(content: string | any): string {
  if (!content) return "";

  // If content is a string, try to parse it as JSON
  let jsonContent = content;
  if (typeof content === "string") {
    try {
      jsonContent = JSON.parse(content);
    } catch (e) {
      // If it's not valid JSON, return as is
      return content;
    }
  }

  // Recursive function to process nodes
  const processNode = (node: any): string => {
    if (!node) return "";

    // If it's a text node, return the text
    if (node.type === "text") {
      return node.text || "";
    }

    // If the node has content (children), process them recursively
    if (node.content && Array.isArray(node.content)) {
      return node.content.map((child: any) => processNode(child)).join(" ");
    }

    return "";
  };

  // Start processing from the root
  if (jsonContent.type === "doc" && jsonContent.content) {
    return jsonContent.content
      .map((node: any) => processNode(node))
      .join(" ")
      .replace(/\s+/g, " ") // Replace multiple spaces with single space
      .trim();
  }

  return "";
}